import $ from "jquery";

/**
 * Interaction events from the Valantic measurement plan (GA4, August 2026).
 *
 *   view_item_list, select_item   trip cards carrying `data-sb-item`
 *   add_to_cart (quote),
 *   to_travel_proposal            request-proposal buttons on a trip page
 *   navigation_use, footer_click  links in the main menu and the footers
 *   contact                       tel:, mailto: and WhatsApp links
 *   filter_use                    the filter overview
 *   trip_search_use               the trip search bar (homepage, destination pages)
 *   search_use, no_search_results site search
 *   faq_click                     opening an FAQ question
 *   photo_zoom, photo_next/previous/last
 *                                 the photo lightbox (visuals overlay)
 *   popup_visible/cta/close       the video overlay, the favourites panel and page overlays
 *
 * Server-rendered events (pageview, view_item, view_destination) are printed in the head
 * by lib/wp/datalayer.php; form events live in gf-form-funnel.js, the direct booking
 * checkout in datalayer-checkout.js and the token route in tracking.js. The spec is docs/claude/datalayer.md.
 */

const push = (data) => window.SbPushToDataLayer(data);

// innerText skips labels hidden per breakpoint (the request button has a mobile and a desktop label).
const text = (element) =>
    ($(element)[0]?.innerText ?? $(element).text()).replace(/\s+/g, " ").trim() ||
    $(element).attr("aria-label") ||
    $(element).attr("title") ||
    "";

const itemsValue = (items) =>
    items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

/**
 * view_item_list: a card counts once per pageload, from 50% visible. Cards that become
 * visible together are pushed as one event per list.
 */
const LIST_FLUSH_DELAY = 250;
const seenCards = new WeakSet();
let pendingLists = {};
let flushTimer = null;

const flushLists = () => {
    Object.values(pendingLists).forEach((items) => {
        window.SbPushEcommerceEvent("view_item_list", { items });
    });

    pendingLists = {};
    flushTimer = null;
};

const cardObserver =
    "IntersectionObserver" in window
        ? new IntersectionObserver(
              (entries) => {
                  entries.forEach((entry) => {
                      if (!entry.isIntersecting || seenCards.has(entry.target)) {
                          return;
                      }

                      seenCards.add(entry.target);
                      cardObserver.unobserve(entry.target);

                      const item = window.SbReadItem(entry.target);
                      if (!item) {
                          return;
                      }

                      const list = item.item_list_id || "unknown";
                      (pendingLists[list] = pendingLists[list] || []).push(item);

                      clearTimeout(flushTimer);
                      flushTimer = setTimeout(flushLists, LIST_FLUSH_DELAY);
                  });
              },
              { threshold: 0.5 },
          )
        : null;

const observeCards = (root) => {
    if (!cardObserver) {
        return;
    }

    $(root)
        .find("[data-sb-item]")
        .addBack("[data-sb-item]")
        .each(function () {
            if (!seenCards.has(this)) {
                cardObserver.observe(this);
            }
        });
};

// The filter overview and the likes overlay inject their cards after load.
const watchInjectedCards = () => {
    if (!("MutationObserver" in window)) {
        return;
    }

    new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === 1) {
                    observeCards(node);
                }
            });
        });
    }).observe(document.body, { childList: true, subtree: true });
};

/**
 * select_item: a click on a card in a list.
 */
$(document).on("click", "a[data-sb-item]", function () {
    const item = window.SbReadItem(this);
    if (!item) {
        return;
    }

    window.SbPushEcommerceEvent("select_item", {
        currency: item.currency,
        value: itemsValue([item]),
        items: [item],
    });
});

/**
 * A request-proposal button on the trip page itself: add_to_cart with add_type quote (with
 * the trip), then to_travel_proposal (without it), as Valantic asked. The button in the
 * menu bar is navigation (navigation_use), also on a trip page.
 */
$(document).on("click", '[data-cta="request-proposal"]', function () {
    if (!window.SbPageItem || $(this).closest(".sb_menu-main").length) {
        return;
    }

    const item = window.SbPageItem;

    window.SbPushEcommerceEvent("add_to_cart", {
        currency: item.currency,
        add_type: "quote",
        value: itemsValue([item]),
        items: [item],
    });

    push({ event: "to_travel_proposal" });
});

/**
 * navigation_use: the overlay menu has two levels: a heading with its links.
 */
$(document).on("click", ".sb_menu-main a", function () {
    const $group = $(this).closest(".sb_menu-main_overlay-menu-primary_group");
    const $heading = $group.find(".sb_menu-main_overlay-menu-primary_heading").first();
    const isSubmenuLink = $group.length > 0 && !$heading.is(this);

    push({
        event: "navigation_use",
        navigation_text: isSubmenuLink ? `${text($heading)} | ${text(this)}` : text(this),
        navigation_level: isSubmenuLink ? 2 : 1,
    });
});

/**
 * footer_click: the column title is part of the text, like the menu hierarchy.
 */
$(document).on("click", '[data-f-type="footer-primary"] a, [data-f-type="footer-secondary"] a', function () {
    const $title = $(this).closest(".sb_footer-primary_middle_column").find(".sb_footer-primary_title").first();

    push({
        event: "footer_click",
        navigation_text: $title.length ? `${text($title)} | ${text(this)}` : text(this),
    });
});

/**
 * contact: share buttons use the same protocols but are not contact attempts.
 */
const CONTACT_PLATFORMS = [
    ["phone", (href) => href.startsWith("tel:")],
    ["email", (href) => href.startsWith("mailto:")],
    ["whatsapp", (href) => /(^|\/\/)(wa\.me|api\.whatsapp\.com)\//.test(href)],
];

$(document).on("click", "a[href]", function () {
    if ($(this).closest(".sb_share-buttons").length) {
        return;
    }

    const href = $(this).attr("href").trim();
    const platform = CONTACT_PLATFORMS.find(([, matches]) => matches(href));

    if (platform) {
        push({ event: "contact", platform: platform[0] });
    }
});

/**
 * filter_use: one push per option the visitor turns on. Groups are English; the option
 * is the filter value (slug or range), which Valantic accepted.
 */
const FILTER_GROUPS = {
    "template-kinds": "kind",
    "template-types": "type",
    "template-periods": "period",
    "days-ranges": "duration",
    "budget-ranges": "budget",
};

$(document).on("change", "#templates-destinations", function () {
    const value = $(this).val();

    if (value && value !== "all") {
        [].concat(value).forEach((option) => {
            push({ event: "filter_use", filter_group: "destination", filter_option: option });
        });
    }
});

$(document).on("change", Object.keys(FILTER_GROUPS).map((name) => `[data-s-type="templates-filter"] input[name="${name}"]`).join(", "), function () {
    if (!this.checked || this.value === "all") {
        return;
    }

    push({ event: "filter_use", filter_group: FILTER_GROUPS[this.name], filter_option: this.value });
});

/**
 * trip_search_use: the search bar submits to the filter overview with a plain GET.
 */
$(document).on("submit", ".sb_search-form", function () {
    const $destination = $(this).find(".sb_search-form_destinations_select option:selected");
    const durations = $(this)
        .find('input[name="days-ranges[]"]:checked')
        .map(function () {
            return text($(this).next("label")) || this.value;
        })
        .get();

    push({
        event: "trip_search_use",
        search_destination: $destination.val() ? text($destination) : "",
        search_duration: durations.join(", "),
    });
});

/**
 * search_use: submitting a query (header overlay or results page), or picking a live
 * result: a trip (pdp), a term (category) or any other page (suggestion).
 */
const searchQuery = () => $("#search-input").val() || new URLSearchParams(window.location.search).get("s") || "";

$(document).on("submit", ".sb_search-overlay_form, .sb_search-results_form", function () {
    push({ event: "search_use", search_action: "search", search_query: $(this).find('[name="s"]').val() || "" });
});

$(document).on("click", "a[data-sb-search-action]", function () {
    push({ event: "search_use", search_action: $(this).attr("data-sb-search-action"), search_query: searchQuery() });
});

/**
 * faq_click: the FAQ component (bound on body) has already toggled the item when this
 * document handler runs, so an expanded head means the question was opened. The expandable
 * texts in `content-combination` use the same component but are SEO copy, not FAQs: no event.
 */
$(document).on("click", ".sb_faq-item_head", function () {
    if ($(this).attr("aria-expanded") !== "true" || $(this).closest('[data-s-type="content-combination"]').length) {
        return;
    }

    push({ event: "faq_click", faq_topic: text($(this).find(".sb_faq-item_question")) });
});

/**
 * photo_zoom: the visitor opens the photo lightbox (accommodation photos, gallery section).
 * A page loaded with a lightbox hash opens it too, but that is no zoom.
 */
$(document).on("click", "[data-visuals-open-overlay], [data-gallery-open-visuals-overlay]", () => {
    push({ event: "photo_zoom" });
});

/**
 * photo_next / photo_previous / photo_last: only slide changes the visitor made (arrow,
 * swipe or arrow key). Opening the lightbox on a slide also changes the slide.
 */
const PHOTO_INTENT_TTL = 1500;
let photoIntent = null;

const setPhotoIntent = (direction) => {
    photoIntent = { direction, at: Date.now() };
};

$(document)
    .on("click", ".sb_visuals-overlay_slider .slick-next", () => setPhotoIntent("next"))
    .on("click", ".sb_visuals-overlay_slider .slick-prev", () => setPhotoIntent("previous"))
    .on("swipe", ".sb_visuals-overlay_slider", (event, slick, direction) => {
        setPhotoIntent(direction === "left" ? "next" : "previous");
    })
    .on("keydown", (event) => {
        if (!$("body").hasClass("s_is-open_visuals-overlay")) {
            return;
        }

        if (event.code === "ArrowRight") {
            setPhotoIntent("next");
        } else if (event.code === "ArrowLeft") {
            setPhotoIntent("previous");
        }
    })
    .on("afterChange", ".sb_visuals-overlay_slider", (event, slick, currentSlide) => {
        if (!photoIntent || Date.now() - photoIntent.at > PHOTO_INTENT_TTL) {
            return;
        }

        push({ event: `photo_${photoIntent.direction}` });
        photoIntent = null;

        if (currentSlide === slick.slideCount - 1) {
            push({ event: "photo_last" });
        }
    });

/**
 * popup_visible / popup_cta / popup_close for the overlays agreed with Valantic: the video
 * overlay, the favourites panel and page overlays. popup_id is `video`, `favorites` or
 * `page_<id>`. A page overlay is an iframe, so clicks inside it are not seen here.
 */
let openPopup = null;

const openPopupEvent = (popupId) => {
    openPopup = popupId;
    push({ event: "popup_visible", popup_id: popupId });
};

const closePopupEvent = (popupId) => {
    if (openPopup !== popupId) {
        return;
    }

    openPopup = null;
    push({ event: "popup_close", popup_id: popupId });
};

$(document)
    .on("open.zf.reveal", "#overlay", function () {
        if ($(this).attr("data-overlay-type") === "video") {
            openPopupEvent("video");
        }
    })
    .on("closed.zf.reveal", "#overlay", () => closePopupEvent("video"))
    .on("click", "#overlay-btn-external", () => {
        if (openPopup === "video") {
            push({ event: "popup_cta", popup_id: "video" });
        }
    })
    .on("open.zf.reveal", "#overlay-page", function () {
        // Both openers set the page URL before opening, as an attribute or as the iframe:
        // <home>/overlay-page/<id>/
        const url = $(this).attr("data-overlay-url") || $(this).find("iframe").attr("src") || "";
        const pageId = url.match(/\/overlay-page\/([^/?]+)/)?.[1];
        if (pageId) {
            openPopupEvent(`page_${pageId}`);
        }
    })
    .on("closed.zf.reveal", "#overlay-page", () => {
        if (openPopup && openPopup.startsWith("page_")) {
            closePopupEvent(openPopup);
        }
    })
    .on("click", "[data-open-likes-overlay]", () => {
        if (openPopup !== "favorites") {
            openPopupEvent("favorites");
        }
    })
    .on("click", "[data-open-overlay-cta]", () => {
        if (openPopup === "favorites") {
            push({ event: "popup_cta", popup_id: "favorites" });
        }
    })
    .on("click keydown", (event) => {
        if (event.type === "keydown" ? event.code !== "Escape" : !$(event.target).closest("[data-close-focus-backdrop]").length) {
            return;
        }

        // Only once the focus backdrop has actually closed the panel.
        setTimeout(() => {
            if (!$("body").hasClass("s_is-open_side-panel")) {
                closePopupEvent("favorites");
            }
        });
    });

$(() => {
    observeCards(document.body);
    watchInjectedCards();

    // no_search_results: the results page itself, not the live results in the overlay.
    if ($('[data-s-type="search-results"] .sb_search-results-items_no-results').length) {
        push({ event: "no_search_results", search_query: searchQuery() });
    }
});
